export type ExtractedLink = {
  text: string;
  href: string;
};

export type ExtractedHeading = {
  level: number;
  text: string;
};

export type ExtractedStructuredData = Record<string, unknown>;

export type ExtractedPageContent = {
  pageTitle: string;
  pageUrl: string;
  pageDescription: string;
  pageLang: string;
  canonicalUrl: string | null;
  siteName: string;
  pageText: string;
  headings: ExtractedHeading[];
  links: ExtractedLink[];
  structuredData: ExtractedStructuredData[];
  contentHash: string;
};

const MAX_PAGE_TEXT_CHARS = 30000;
const MAX_LINKS = 100;
const MAX_HEADINGS = 60;
const MAX_STRUCTURED_BLOCKS = 10;

const IGNORED_TAGS = ["script", "style", "noscript", "template", "svg", "canvas", "iframe"];

const NON_CONTENT_SELECTORS = [
  "[aria-hidden='true']",
  "[hidden]",
  ".visually-hidden",
  ".sr-only",
  ".screen-reader-only",
  "[role='banner']",
  "[role='contentinfo']",
  "[role='navigation']",
  ".cookie-banner",
  ".cookie-consent",
  "#cookie-banner",
  "#cookie-consent",
  "#onetrust-banner-sdk",
  "[data-cookieconsent]",
  "[data-cookiefirst-root]",
  "#CybotCookiebotDialog",
];

const READABLE_ROOT_SELECTORS = [
  "main",
  "article",
  "[role='main']",
  "#main",
  "#content",
  "#main-content",
  ".main-content",
  ".post-content",
  ".article-content",
  ".entry-content",
];

function isVisible(element: Element): boolean {
  if (!(element instanceof HTMLElement)) {
    return true;
  }
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return false;
  }
  return true;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function readMeta(...names: string[]): string {
  for (const name of names) {
    for (const attr of ["name", "property", "itemprop"]) {
      const el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
      const content = el?.getAttribute("content");
      if (content && content.trim()) {
        return content.trim();
      }
    }
  }
  return "";
}

function readCanonical(): string | null {
  const link = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
  return link?.href || null;
}

function readStructuredData(): ExtractedStructuredData[] {
  const blocks = Array.from(
    document.querySelectorAll<HTMLScriptElement>("script[type='application/ld+json']"),
  );
  const results: ExtractedStructuredData[] = [];

  for (const block of blocks) {
    if (results.length >= MAX_STRUCTURED_BLOCKS) {
      break;
    }
    try {
      const parsed = JSON.parse(block.textContent ?? "");
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (item && typeof item === "object") {
            results.push(item as ExtractedStructuredData);
          }
        }
      } else if (parsed && typeof parsed === "object") {
        results.push(parsed as ExtractedStructuredData);
      }
    } catch {
      // ignore malformed JSON-LD
    }
  }

  return results.slice(0, MAX_STRUCTURED_BLOCKS);
}

function scoreCandidate(element: Element): number {
  const text = element.textContent?.trim() ?? "";
  if (text.length < 40) {
    return 0;
  }
  const linkCount = element.querySelectorAll("a").length;
  const blockCount = element.querySelectorAll("p, li, article, section, h1, h2, h3, h4, h5, h6").length;
  return text.length + blockCount * 50 - linkCount * 25;
}

function getReadableRoot(): HTMLElement {
  const candidates: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  for (const selector of READABLE_ROOT_SELECTORS) {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      if (!seen.has(element) && isVisible(element)) {
        seen.add(element);
        candidates.push(element);
      }
    });
  }

  let best: HTMLElement = document.body;
  let bestScore = scoreCandidate(document.body) * 0.5;

  for (const candidate of candidates) {
    const score = scoreCandidate(candidate);
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  return best;
}

function expandCollapsibleContent(root: HTMLElement) {
  root.querySelectorAll<HTMLDetailsElement>("details").forEach((details) => {
    details.setAttribute("open", "");
  });

  root
    .querySelectorAll<HTMLElement>("[aria-expanded='false']")
    .forEach((element) => {
      const controls = element.getAttribute("aria-controls");
      if (controls) {
        const target = root.ownerDocument?.getElementById(controls);
        if (target) {
          target.removeAttribute("hidden");
        }
      }
    });
}

function inlineImageAlts(root: HTMLElement) {
  root.querySelectorAll<HTMLImageElement>("img[alt]").forEach((img) => {
    const alt = img.getAttribute("alt");
    if (alt && alt.trim()) {
      const span = root.ownerDocument!.createElement("span");
      span.textContent = ` [image: ${alt.trim()}] `;
      img.replaceWith(span);
    }
  });
}

function inlineButtonText(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>("button, [role='button'], a[role='button']").forEach((button) => {
    const ariaLabel = button.getAttribute("aria-label");
    const fallback = button.textContent ?? "";
    const label = ariaLabel || fallback;
    const normalized = normalizeText(label);
    if (normalized && (!fallback || normalizeText(fallback) !== normalized)) {
      button.textContent = ` ${normalized} `;
    }
  });
}

function dropInvisibleDescendants(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>("*").forEach((element) => {
    if (!isVisible(element)) {
      element.remove();
    }
  });
}

function extractTextWithSemantics(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;

  IGNORED_TAGS.forEach((tag) => {
    clone.querySelectorAll(tag).forEach((element) => element.remove());
  });

  NON_CONTENT_SELECTORS.forEach((selector) => {
    clone.querySelectorAll(selector).forEach((element) => element.remove());
  });

  expandCollapsibleContent(clone);
  dropInvisibleDescendants(clone);
  inlineImageAlts(clone);
  inlineButtonText(clone);

  return normalizeText(clone.textContent ?? "").slice(0, MAX_PAGE_TEXT_CHARS);
}

function extractHeadings(): ExtractedHeading[] {
  return Array.from(document.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6"))
    .filter((element) => isVisible(element))
    .map((element) => ({
      level: Number(element.tagName.substring(1)),
      text: normalizeText(element.textContent ?? "").slice(0, 200),
    }))
    .filter((heading) => heading.text.length > 0)
    .slice(0, MAX_HEADINGS);
}

function extractLinks(): ExtractedLink[] {
  return Array.from(document.links)
    .filter((link) => Boolean(link.href) && !link.href.startsWith("javascript:"))
    .map((link) => ({
      text: normalizeText(link.textContent ?? link.href).slice(0, 200),
      href: link.href,
    }))
    .filter(
      (link, index, list) => list.findIndex((candidate) => candidate.href === link.href) === index,
    )
    .slice(0, MAX_LINKS);
}

export async function extractPageContent(): Promise<ExtractedPageContent> {
  const root = getReadableRoot();
  const pageText = extractTextWithSemantics(root);
  const headings = extractHeadings();
  const links = extractLinks();
  const structuredData = readStructuredData();

  const pageTitle = document.title || "Untitled page";
  const pageUrl = window.location.href;
  const pageDescription =
    readMeta("description", "og:description", "twitter:description") || "";
  const pageLang = document.documentElement.getAttribute("lang") ?? "";
  const canonicalUrl = readCanonical();
  const siteName = readMeta("og:site_name", "application-name") || "";

  const hashSource = JSON.stringify({
    pageTitle,
    pageUrl,
    pageDescription,
    canonicalUrl,
    pageText,
    headings: headings.map((heading) => `${heading.level}:${heading.text}`).join("|"),
    links: links.map((link) => link.href).join("|"),
    structuredCount: structuredData.length,
  });

  return {
    pageTitle,
    pageUrl,
    pageDescription,
    pageLang,
    canonicalUrl,
    siteName,
    pageText,
    headings,
    links,
    structuredData,
    contentHash: await sha256(hashSource),
  };
}
